import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Slider,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  TextField,
  Paper,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SettingsIcon from "@mui/icons-material/Settings";
import MagicIcon from "@mui/icons-material/AutoFixHigh";
import DownloadIcon from "@mui/icons-material/Download";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ShareIcon from "@mui/icons-material/Share";
import BrightnessIcon from "@mui/icons-material/Brightness6";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import InfoIcon from "@mui/icons-material/Info";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import landingIcon from "./landingicon1.png";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const theme = createTheme({
  typography: {
    h2: {
      fontSize: "3.5rem",
      "@media (max-width:600px)": {
        fontSize: "2.5rem",
      },
    },
    h5: {
      fontSize: "1.5rem",
      "@media (max-width:600px)": {
        fontSize: "1.2rem",
      },
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.875rem",
          padding: "8px 12px",
        },
      },
    },
  },
});

const LandingPage = () => {
  // State Management
  const [image, setImage] = useState(null);
  const [columns, setColumns] = useState(3);
  const [splitImages, setSplitImages] = useState([]);
  const [filter, setFilter] = useState("none");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [quality, setQuality] = useState("high");
  const [showPreview, setShowPreview] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [imageAspectRatio, setImageAspectRatio] = useState(1);

  // Dropzone Configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      validateAndLoadImage(file);
    },
    maxSize: 10485760, // 10MB
    multiple: false,
  });

  // Image Validation and Loading
  const validateAndLoadImage = (file) => {
    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        setImageSize({ width: img.width, height: img.height });
        setImageAspectRatio(aspectRatio);

        if (Math.abs(aspectRatio - 1) > 0.1) {
          setError(
            "Warning: Image is not square. This might affect grid appearance."
          );
        }

        if (img.width < 1080 || img.height < 1080) {
          setError(
            "Warning: Image resolution is lower than recommended for Instagram (1080x1080)"
          );
        }

        setImage(reader.result);
        setLoading(false);
      };

      img.onerror = () => {
        setError("Error loading image. Please try another file.");
        setLoading(false);
      };

      img.src = reader.result;
    };

    reader.onerror = () => {
      setError("Error reading file. Please try again.");
      setLoading(false);
    };

    reader.readAsDataURL(file);
  };

  // Image Processing Functions
  const handleSplitImage = async () => {
    setLoading(true);
    try {
      const img = new Image();
      img.src = image;
      await new Promise((resolve) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const splitWidth = img.width / columns;
          const splitHeight = splitWidth;

          let newImages = [];

          // Apply transformations and split image
          for (let y = 0; y < img.height; y += splitHeight) {
            for (let x = 0; x < img.width; x += splitWidth) {
              canvas.width = splitWidth;
              canvas.height = splitHeight;

              ctx.save();
              ctx.translate(canvas.width / 2, canvas.height / 2);
              ctx.rotate((rotation * Math.PI) / 180);
              ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) ${getFilterStyle()}`;

              ctx.drawImage(
                img,
                x,
                y,
                splitWidth,
                splitHeight,
                -splitWidth / 2,
                -splitHeight / 2,
                splitWidth,
                splitHeight
              );

              ctx.restore();

              const quality = getQualityValue();
              newImages.push(canvas.toDataURL("image/jpeg", quality));
            }
          }

          setSplitImages(newImages);
          setShowPreview(true);
          setSnackbar({ open: true, message: "Grid generated successfully!" });
          resolve();
        };
      });
    } catch (error) {
      setError("Error processing image. Please try again.");
      setSnackbar({ open: true, message: "Error generating grid" });
    } finally {
      setLoading(false);
    }
  };

  // Helper Functions
  const getFilterStyle = () => {
    switch (filter) {
      case "grayscale":
        return "grayscale(100%)";
      case "sepia":
        return "sepia(100%)";
      case "invert":
        return "invert(100%)";
      case "contrast":
        return "contrast(150%)";
      default:
        return "";
    }
  };

  const getQualityValue = () => {
    const qualityMap = {
      high: 1.0,
      medium: 0.7,
      low: 0.4,
    };
    return qualityMap[quality] || 0.7;
  };
  // Download Functions
  const handleDownloadZip = async () => {
    try {
      setLoading(true);
      const zip = new JSZip();
      const folder = zip.folder("instagram_grid_images");

      splitImages.forEach((src, index) => {
        const base64Data = src.replace(
          /^data:image\/(png|jpg|jpeg);base64,/,
          ""
        );
        folder.file(`grid_image_${index + 1}.jpg`, base64Data, {
          base64: true,
        });
      });

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "instagram_grid_images.zip");
      setSnackbar({ open: true, message: "Download started!" });
    } catch (error) {
      setError("Error creating download. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSingle = (src, index) => {
    const link = document.createElement("a");
    link.href = src;
    link.download = `grid_image_${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sharing Functions
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Instagram Grid Creation",
          text: "Check out my Instagram grid created with Grid Splitter!",
          url: window.location.href,
        });
      } else {
        const shareUrl = encodeURIComponent(window.location.href);
        window.open(
          `https://twitter.com/intent/tweet?url=${shareUrl}&text=Created%20my%20Instagram%20grid!`
        );
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error sharing content",
      });
    }
  };

  // Reset Function
  const handleReset = () => {
    setImage(null);
    setColumns(3);
    setSplitImages([]);
    setFilter("none");
    setRotation(0);
    setBrightness(100);
    setContrast(100);
    setQuality("high");
    setShowPreview(false);
    setError(null);
    setImageSize({ width: 0, height: 0 });
    setImageAspectRatio(1);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="lg"
        sx={{
          mt: 5,
          padding: { xs: "20px", sm: "30px", md: "50px" },
          backgroundColor: "#f9f9f9",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header Section */}
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={6}>
            <ThemeProvider theme={theme}>
              <Typography
                variant="h2"
                fontWeight={700}
                color={"#9c27b0"}
                gutterBottom
              >
                Online Image Splitter for your Instagram Grids
              </Typography>
              <Typography variant="h5" gutterBottom>
                Free Instagram Grid Maker
              </Typography>
            </ThemeProvider>
            <Typography
              sx={{
                mt: 2,
                fontWeight: "bold",
                fontSize: { xs: "1.2rem", md: "1.4rem" },
              }}
            >
              Upload an image from your device to begin
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <img
              src={landingIcon}
              alt="Instagram Splitter"
              style={{ maxWidth: "100%", borderRadius: "8px" }}
            />
          </Grid>
        </Grid>

        {/* Upload Section */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            textAlign: "center",
          }}
        >
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "secondary.main" : "grey.300",
              borderRadius: 2,
              p: 4,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "secondary.main",
                backgroundColor: "rgba(156, 39, 176, 0.04)",
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadFileIcon
              sx={{ fontSize: 48, color: "secondary.main", mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              {isDragActive
                ? "Drop your image here"
                : "Drag & drop your image here or click to select"}
            </Typography>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Image Editor Section */}
        {image && (
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={4}>
              {/* Preview Section */}
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: "12px" }}>
                  <Typography variant="h6" gutterBottom>
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={image}
                      alt="Preview"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        transform: `rotate(${rotation}deg)`,
                        filter: `brightness(${brightness}%) contrast(${contrast}%) ${getFilterStyle()}`,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Controls Section */}
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: "12px" }}>
                  <Typography variant="h6" gutterBottom>
                    Edit Settings
                  </Typography>

                  {/* Control Sliders */}
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography gutterBottom>Grid Columns</Typography>
                      <Slider
                        value={columns}
                        onChange={(e, val) => setColumns(val)}
                        min={1}
                        max={6}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography gutterBottom>Rotation</Typography>
                      <Slider
                        value={rotation}
                        onChange={(e, val) => setRotation(val)}
                        min={0}
                        max={360}
                        step={90}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography gutterBottom>Brightness</Typography>
                      <Slider
                        value={brightness}
                        onChange={(e, val) => setBrightness(val)}
                        min={0}
                        max={200}
                        valueLabelDisplay="auto"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography gutterBottom>Contrast</Typography>
                      <Slider
                        value={contrast}
                        onChange={(e, val) => setContrast(val)}
                        min={0}
                        max={200}
                        valueLabelDisplay="auto"
                      />
                    </Grid>

                    {/* Filter and Quality Selectors */}
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Filter</InputLabel>
                        <Select
                          value={filter}
                          onChange={(e) => setFilter(e.target.value)}
                          label="Filter"
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="grayscale">Grayscale</MenuItem>
                          <MenuItem value="sepia">Sepia</MenuItem>
                          <MenuItem value="invert">Invert</MenuItem>
                          <MenuItem value="contrast">High Contrast</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Quality</InputLabel>
                        <Select
                          value={quality}
                          onChange={(e) => setQuality(e.target.value)}
                          label="Quality"
                        >
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <MagicIcon />
                      }
                      onClick={handleSplitImage}
                      disabled={loading}
                    >
                      Generate Grid
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleReset}
                      disabled={loading}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<ShareIcon />}
                      onClick={handleShare}
                      disabled={loading}
                    >
                      Share
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Results Section */}
            {splitImages.length > 0 && (
              <Paper
                elevation={2}
                sx={{
                  mt: 4,
                  p: 2,
                  borderRadius: "12px",
                  maxWidth: "800px", // Limit maximum width
                  margin: "2rem auto", // Center the paper
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontSize: "1.5rem",
                    textAlign: "center",
                    // fontStyle: "bold",
                    mb: 2,
                  }}
                >
                  Your Grid Images
                </Typography>

                <Grid
                  container
                  spacing={1.5} // Reduced spacing between grid items
                  sx={{
                    maxWidth: "600px", // Limit grid width
                    margin: "0 auto", // Center the grid
                  }}
                >
                  {splitImages.map((src, index) => (
                    <Grid
                      item
                      xs={4}
                      key={index}
                      sx={{
                        aspectRatio: "1/1", // Maintain square aspect ratio
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          "&:hover .download-button": {
                            opacity: 1,
                          },
                        }}
                      >
                        <img
                          src={src}
                          alt={`Grid ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "6px",
                          }}
                        />
                        <IconButton
                          onClick={() => handleDownloadSingle(src, index)}
                          className="download-button"
                          size="small" // Smaller download button
                          sx={{
                            position: "absolute",
                            bottom: 4,
                            right: 4,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            opacity: 0, // Hide by default
                            transition: "opacity 0.2s ease",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: "1.2rem" }} />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small" // Smaller button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadZip}
                    disabled={loading}
                    sx={{
                      mt: 1,
                      textTransform: "none", // Remove uppercase transform
                      borderRadius: "6px",
                      padding: "6px 16px",
                    }}
                  >
                    Download All Images
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {/* Instructions Section */}
        <Box sx={{ mt: 8, textAlign: "center" }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            How to split photos into an IG grid?
          </Typography>
          <Typography variant="body1" fontWeight={300} gutterBottom>
            Here is how the Instagram Grid Creator works:
          </Typography>
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: "center" }}>
              <UploadFileIcon fontSize="large" />
              <Typography variant="h6" color={"#9c27b0"} fontWeight={700}>
                Step 1
              </Typography>
              <Typography marginBottom={1} fontWeight={700}>
                Choose Image or Graphic
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Upload an image graphic from Canva (see the recommended
                dimensions).
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: "center" }}>
              <SettingsIcon fontSize="large" />
              <Typography variant="h6" color={"#9c27b0"} fontWeight={700}>
                Step 2
              </Typography>
              <Typography marginBottom={1} fontWeight={700}>
                Set your Options
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Choose how many columns you want to cut your image in. (Standard
                instagram grid is 3-column)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: "center" }}>
              <MagicIcon fontSize="large" />
              <Typography variant="h6" color={"#9c27b0"} fontWeight={700}>
                Step 3
              </Typography>
              <Typography marginBottom={1} fontWeight={700}>
                The split magic happens
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Our tool cuts your grid image in the standard 3-column Instagram
                squares.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: "center" }}>
              <DownloadIcon fontSize="large" />
              <Typography variant="h6" color={"#9c27b0"} fontWeight={700}>
                Step 4
              </Typography>
              <Typography marginBottom={1} fontWeight={700}>
                Download your Instagram Posts
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                We'll show you a preview of how the posts will look like in the
                grid.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    </ThemeProvider>
  );
};

export default LandingPage;
